import {NgTemplateOutlet} from "@angular/common"
import {Component, computed, forwardRef, inject, OnInit, signal, ChangeDetectionStrategy, model} from "@angular/core"
import {MatTooltipModule} from "@angular/material/tooltip"
import {Settings} from "@app/common/models/settings/settings"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {getNodeIconClass} from "@app/template-editor/helpers/template-icons"
import {TemplateNodeClipboardService} from "@app/template-editor/services/template-node-clipboard.service"
import {
    ConfigGroup,
    ConfigInfo,
    getInterfaceIdPrefix,
    ImageInfo,
    imageLike,
    isValidExternalId,
    MaterialInfo,
    materialLike,
    numberLike,
    stringLike,
    StringResolve,
    TemplateInstance,
    wrapInterfaceId,
    fundamentalTypes,
    NodesInfo,
    fundamentalLikeNode,
    isFundamentalLikeNode,
    isAnyJsonValue,
    ObjectPathPart,
    objectPathStartsWith,
} from "@cm/template-nodes"
import {z} from "zod/v4-mini"
import {TemplateInstanceOutputComponent} from "@app/template-editor/components/template-instance-output/template-instance-output.component"
import {SelectionPossibilities, SelectionPossibility, ValueSlotComponent} from "@app/template-editor/components/value-slot/value-slot.component"
import {BaseInspectorComponent} from "@app/template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@app/template-editor/components/inspectors/inspector-section/inspector-section.component"
import {MatMenuModule} from "@angular/material/menu"
import {TemplateThumbnailComponent} from "@template-editor/components/template-thumbnail/template-thumbnail.component"

type ParameterInput = {
    key: string
    label: string
    icon?: string
    schema: z.ZodMiniType<unknown, unknown>
    selectionPossibilities?: SelectionPossibilities<unknown>
    overwrittenValue: unknown
    specialType?: "variant" | "material" | "image"
    throttledUpdate?: boolean
    delta?: number
    min?: number
    max?: number
    numberModifier?: "slider" | "arrows"
    subParameters?: Omit<ParameterInput, "subParameters">[]
    affectedObjectPathParts?: ObjectPathPart[]
    sectionTitle?: string
}

const stringLikeOptional = z.optional(stringLike)
const numberLikeOptional = z.optional(numberLike)
const materialLikeOptional = z.optional(materialLike)
const imageLikeOptional = z.optional(imageLike)
const fundamentalLikeNodesOptional = z.optional(z.array(fundamentalLikeNode))
const fundamentalTypeOptionalSchemas = Object.values(fundamentalTypes).map((ft) => ({InfoClass: ft.InfoClass, schema: z.optional(ft.zodLike)}))

type SectionEntry = {kind: "item"; config: ParameterInput} | {kind: "section"; title: string; configs: ParameterInput[]}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-instance-inspector",
    templateUrl: "./template-instance-inspector.component.html",
    styleUrl: "./template-instance-inspector.component.scss",
    imports: [
        InspectorSectionComponent,
        forwardRef(() => ValueSlotComponent),
        TemplateInstanceOutputComponent,
        MatTooltipModule,
        MatMenuModule,
        TemplateThumbnailComponent,
        NgTemplateOutlet,
    ],
})
export class TemplateInstanceInspectorComponent extends BaseInspectorComponent<TemplateInstance> implements OnInit {
    readonly $isValidExternalId = signal(isValidExternalId)
    readonly $isActive = computed(() => this.sceneManagerService.isNodeActive(this.$node()))
    readonly $filterBySelection = model(true, {alias: "filterBySelection"})

    protected readonly $allDescriptors = computed(() => this.sceneManagerService.getDescriptorsForNode(this.$node()))
    protected readonly $descriptors = computed(() => {
        const selectedObjectPathParts = this.sceneManagerService.$selectedObjectPathParts()
        const descriptors = this.$allDescriptors()
        if (!this.$filterBySelection()) return descriptors
        return descriptors.filter((descriptor) => {
            const {affectedObjectPathParts} = descriptor.props
            if (!affectedObjectPathParts) return true

            const filteredAffectedObjectPathParts = affectedObjectPathParts.filter(
                ({path}) => !affectedObjectPathParts.some(({path: otherPath}) => otherPath.length > path.length && objectPathStartsWith(otherPath, path)),
            )

            return filteredAffectedObjectPathParts.some((affected) => {
                return selectedObjectPathParts.some((selected) => {
                    if (affected.path.length < selected.path.length) {
                        return objectPathStartsWith(selected.path, affected.path)
                    } else if (affected.path.length > selected.path.length) {
                        return objectPathStartsWith(affected.path, selected.path)
                    } else {
                        return objectPathStartsWith(affected.path, selected.path) && affected.part === selected.part
                    }
                })
            })
        })
    })

    readonly $template = computed(() => {
        const {template} = this.$parameters()
        return template
    })

    readonly $unusedParameters = computed(() => {
        const {parameters} = this.$parameters()
        const inputDescriptors = this.$allDescriptors().filter((descriptor) => descriptor.props.type === "input")
        const descriptorKeys = inputDescriptors.map((descriptor) => {
            const [, descriptorKey] = getInterfaceIdPrefix(descriptor.props.id)
            return descriptorKey
        })

        return Object.keys(parameters.parameters).filter((key) => !descriptorKeys.includes(key))
    })
    readonly $unusedInputs = computed(() => {
        const unusedParameters = this.$unusedParameters()
        const {parameters} = this.$parameters()
        return unusedParameters.map((key) => ({
            key,
            label: key,
            schema: fundamentalTypeOptionalSchemas.find((ft) => ft.schema.safeParse(parameters.parameters[key]).success)?.schema,
        }))
    })

    private readonly clipboardService = inject(TemplateNodeClipboardService)
    private readonly notificationsService = inject(NotificationsService)
    readonly $inputs = computed<ParameterInput[]>(() => {
        const parameters = this.sceneManagerService.$instanceParameters()
        const id = this.$parameters().id

        const overwrittenValue = (key: string) => {
            const identifier = wrapInterfaceId(id, key)
            return parameters.parameters[identifier]
        }

        return this.$descriptors()
            .filter((descriptor) => descriptor.props.type === "input")
            .map<ParameterInput>((descriptor) => {
                const [, key] = getInterfaceIdPrefix(descriptor.props.id)

                if (descriptor instanceof ConfigInfo) {
                    return {
                        key,
                        label: descriptor.props.name,
                        icon: getNodeIconClass(ConfigGroup.getNodeClass()),
                        schema: stringLikeOptional,
                        selectionPossibilities: descriptor.props.variants.map<SelectionPossibility<unknown>>((variant) => ({
                            name: variant.name,
                            value: variant.id,
                            actions: [
                                {
                                    iconClass: "far fa-copy",
                                    toolTip: "Copy",
                                    fn: () => {
                                        this.clipboardService.copy(
                                            new StringResolve({
                                                parameters: {
                                                    name: `ConfigRef: ${variant.name}`,
                                                    template: this.$node().parameters.template,
                                                    nodeId: variant.id,
                                                },
                                            }),
                                        )
                                        this.notificationsService.showInfo("Copied config reference to clipboard")
                                    },
                                },
                            ],
                        })),
                        overwrittenValue: overwrittenValue(key),
                        specialType: "variant",
                        affectedObjectPathParts: descriptor.props.affectedObjectPathParts,
                        sectionTitle: descriptor.props.sectionTitle,
                    }
                } else if (descriptor instanceof MaterialInfo) {
                    return {
                        key,
                        label: descriptor.props.name,
                        schema: materialLikeOptional,
                        overwrittenValue: overwrittenValue(key),
                        specialType: "material",
                        subParameters: [
                            {
                                key: `${key}-horizontalOffset`,
                                label: "Horizontal",
                                schema: numberLikeOptional,
                                overwrittenValue: overwrittenValue(`${key}.horizontalOffset`),
                                throttledUpdate: true,
                                delta: 0.03,
                                numberModifier: "arrows",
                            },
                            {
                                key: `${key}-verticalOffset`,
                                label: "Vertical",
                                schema: numberLikeOptional,
                                overwrittenValue: overwrittenValue(`${key}.verticalOffset`),
                                throttledUpdate: true,
                                delta: 0.03,
                                numberModifier: "arrows",
                            },
                            {
                                key: `${key}-rotation`,
                                label: "Rotation",
                                schema: numberLikeOptional,
                                overwrittenValue: overwrittenValue(`${key}.rotation`),
                                throttledUpdate: true,
                                delta: 0.03,
                                numberModifier: "arrows",
                            },
                        ],
                        affectedObjectPathParts: descriptor.props.affectedObjectPathParts,
                    }
                } else if (descriptor instanceof ImageInfo) {
                    return {
                        key,
                        label: descriptor.props.name,
                        schema: imageLikeOptional,
                        overwrittenValue: overwrittenValue(key),
                        specialType: "image",
                        affectedObjectPathParts: descriptor.props.affectedObjectPathParts,
                    }
                } else if (descriptor instanceof NodesInfo) {
                    return {
                        key,
                        label: descriptor.props.name,
                        schema: fundamentalLikeNodesOptional,
                        overwrittenValue: overwrittenValue(key),
                        affectedObjectPathParts: descriptor.props.affectedObjectPathParts,
                    }
                } else {
                    const schema = fundamentalTypeOptionalSchemas.find((ft) => descriptor instanceof ft.InfoClass)?.schema
                    if (!schema) throw new Error("Invalid descriptor type")
                    return {
                        key,
                        label: descriptor.props.name,
                        schema,
                        overwrittenValue: overwrittenValue(key),
                        affectedObjectPathParts: descriptor.props.affectedObjectPathParts,
                    }
                }
            })
    })
    readonly $inputsFiltered = computed(
        () =>
            this.$descriptors().filter((descriptor) => descriptor.props.type === "input").length !==
            this.$allDescriptors().filter((descriptor) => descriptor.props.type === "input").length,
    )

    readonly $variantInputs = computed(() => this.$inputs().filter((input) => input.specialType === "variant"))
    readonly $clusteredVariantInputs = computed(() => {
        const configs = this.$variantInputs()

        const indicesByTitle = new Map<string, number[]>()
        configs.forEach((config, i) => {
            const {sectionTitle} = config
            if (sectionTitle) {
                const list = indicesByTitle.get(sectionTitle) ?? []
                list.push(i)
                indicesByTitle.set(sectionTitle, list)
            }
        })

        const entries: SectionEntry[] = []
        for (let i = 0; i < configs.length; i++) {
            const config = configs[i]
            const {sectionTitle} = config
            if (sectionTitle) {
                const indices = indicesByTitle.get(sectionTitle)!
                if (i === indices[0]) {
                    entries.push({
                        kind: "section",
                        title: sectionTitle,
                        configs: indices.map((idx) => configs[idx]),
                    })
                }
            } else {
                entries.push({kind: "item", config})
            }
        }
        return entries
    })
    readonly $unsectionedVariantInputs = computed(() =>
        this.$clusteredVariantInputs()
            .filter((input) => input.kind === "item")
            .map((entry) => entry.config),
    )
    readonly $sectionedVariantInputs = computed(() => this.$clusteredVariantInputs().filter((input) => input.kind === "section"))

    readonly $slotInputs = computed(() => {
        return [...this.$unsectionedVariantInputs(), ...this.$inputs().filter((input) => !input.specialType)]
    })
    readonly $nonSlotInputs = computed(() => {
        return this.$inputs().filter((input) => input.specialType === "image" || input.specialType === "material")
    })
    readonly $outputs = computed(() => {
        return this.$descriptors().filter((descriptor) => descriptor.props.type === "output")
    })
    readonly $outputsFiltered = computed(
        () =>
            this.$descriptors().filter((descriptor) => descriptor.props.type === "output").length !==
            this.$allDescriptors().filter((descriptor) => descriptor.props.type === "output").length,
    )

    updateOverwrittenValue(key: string, value: unknown) {
        if (value === undefined || isAnyJsonValue(value) || isFundamentalLikeNode(value)) {
            const parameters = this.sceneManagerService.$instanceParameters().clone({cloneSubNode: () => true})
            const id = this.$parameters().id
            const identifier = wrapInterfaceId(id, key)

            parameters.updateParameters({[identifier]: value})
            this.sceneManagerService.$instanceParameters.set(parameters)
        }
    }

    onChangedInputSideEffect(parameterInputKey: string) {
        return (value: unknown) => {
            const {parameters} = this.$parameters()
            const parametersClone = parameters.clone()
            const newParams = {...parametersClone.parameters}
            Object.keys(newParams).forEach((key) => {
                if (key.startsWith(parameterInputKey)) {
                    if (value === undefined || value === null || key !== parameterInputKey) {
                        delete newParams[key]
                    }
                }
            })

            parameters.replaceParameters(newParams)
        }
    }

    onMouseEnter(parameterInput: {affectedObjectPathParts?: ObjectPathPart[]}) {
        const affectedObjectPathParts = parameterInput.affectedObjectPathParts ?? []
        this.sceneManagerService.hoverNodes(
            affectedObjectPathParts.filter(
                ({path}) => !affectedObjectPathParts.some(({path: otherPath}) => otherPath.length > path.length && objectPathStartsWith(otherPath, path)),
            ),
        )
    }

    onMouseLeave() {
        this.sceneManagerService.hoverNodes(undefined)
    }

    protected Settings = Settings
}
